import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Loader2, Lock } from "lucide-react";

interface Prerequisite {
  id: string;
  required_path_id: string;
  required_path_title: string;
  required_status: "completed" | "in_progress";
}

interface PublishedPath {
  id: string;
  title: string;
}

interface PathPrerequisitesEditorProps {
  /** ID du parcours dont on gère les prérequis (le parcours « bloqué ») */
  pathId: string | undefined;
  disabled?: boolean;
}

const STATUS_LABELS: Record<string, string> = {
  completed: "Terminé",
  in_progress: "En cours (ou terminé)",
};

export function PathPrerequisitesEditor({ pathId, disabled = false }: PathPrerequisitesEditorProps) {
  const [prerequisites, setPrerequisites] = useState<Prerequisite[]>([]);
  const [publishedPaths, setPublishedPaths] = useState<PublishedPath[]>([]);
  // All path_prerequisite rows globally (to detect cycles)
  const [allRules, setAllRules] = useState<{ blocked_path_id: string; required_path_id: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<Prerequisite | null>(null);
  const [removing, setRemoving] = useState(false);

  // Form state for new rule
  const [newRequiredPathId, setNewRequiredPathId] = useState<string>("");
  const [newRequiredStatus, setNewRequiredStatus] = useState<"completed" | "in_progress">("completed");
  const [adding, setAdding] = useState(false);

  const fetchData = useCallback(async () => {
    if (!pathId) return;
    setLoading(true);
    try {
      const [preqRes, pathsRes, allRulesRes] = await Promise.all([
        (supabase as any)
          .from("path_prerequisite")
          .select("id, required_path_id, required_status, path!path_prerequisite_required_path_id_fkey(id, title)")
          .eq("blocked_path_id", pathId),
        supabase
          .from("path")
          .select("id, title, path_root_id, version")
          .eq("is_published", true)
          .eq("path_type", "path")
          .order("title"),
        // Load all rules to detect cycles (point 3)
        (supabase as any)
          .from("path_prerequisite")
          .select("blocked_path_id, required_path_id"),
      ]);

      if (preqRes.error) throw preqRes.error;
      if (pathsRes.error) throw pathsRes.error;
      // allRulesRes error is non-fatal

      // deduplicate published paths: latest version per root
      const byRoot = new Map<string, PublishedPath>();
      for (const p of pathsRes.data || []) {
        const key = (p as any).path_root_id || p.id;
        if (!byRoot.has(key)) byRoot.set(key, { id: p.id, title: p.title });
      }
      setPublishedPaths(
        Array.from(byRoot.values()).sort((a, b) => a.title.localeCompare(b.title)),
      );

      setPrerequisites(
        (preqRes.data || []).map((r: any) => ({
          id: r.id,
          required_path_id: r.required_path_id,
          required_path_title: r.path?.title ?? r.required_path_id,
          required_status: r.required_status,
        })),
      );

      setAllRules(
        (allRulesRes.data || []).map((r: any) => ({
          blocked_path_id: r.blocked_path_id,
          required_path_id: r.required_path_id,
        })),
      );
    } catch (e: any) {
      toast.error(e.message || "Erreur lors du chargement des prérequis");
    } finally {
      setLoading(false);
    }
  }, [pathId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Point 3 — Cycle detection.
   * Adding the rule "pathId requires candidateId" creates a cycle if there is
   * already a path from candidateId back to pathId through existing rules.
   */
  const wouldCreateCycle = (candidateId: string): boolean => {
    if (!pathId) return false;
    // BFS from candidateId through "blocked_path_id requires required_path_id"
    // to see if we reach pathId.
    const visited = new Set<string>();
    const queue: string[] = [candidateId];
    while (queue.length) {
      const current = queue.shift()!;
      if (current === pathId) return true;
      if (visited.has(current)) continue;
      visited.add(current);
      // What does `current` require?
      for (const rule of allRules) {
        if (rule.blocked_path_id === current && !visited.has(rule.required_path_id)) {
          queue.push(rule.required_path_id);
        }
      }
    }
    return false;
  };

  const handleAdd = async () => {
    if (!pathId || !newRequiredPathId) return;

    if (prerequisites.some((p) => p.required_path_id === newRequiredPathId)) {
      toast.error("Ce parcours est déjà un prérequis.");
      return;
    }
    if (newRequiredPathId === pathId) {
      toast.error("Un parcours ne peut pas être son propre prérequis.");
      return;
    }
    if (wouldCreateCycle(newRequiredPathId)) {
      toast.error(
        "Cette règle crée une dépendance circulaire : A requiert B qui requiert A (directement ou indirectement).",
      );
      return;
    }

    setAdding(true);
    try {
      const { data, error } = await (supabase as any)
        .from("path_prerequisite")
        .insert({
          blocked_path_id: pathId,
          required_path_id: newRequiredPathId,
          required_status: newRequiredStatus,
        })
        .select("id, required_path_id, required_status, path!path_prerequisite_required_path_id_fkey(id, title)")
        .single();

      if (error) throw error;

      const newPrereq: Prerequisite = {
        id: data.id,
        required_path_id: data.required_path_id,
        required_path_title: (data as any).path?.title ?? data.required_path_id,
        required_status: data.required_status,
      };
      setPrerequisites((prev) => [...prev, newPrereq]);
      setAllRules((prev) => [
        ...prev,
        { blocked_path_id: pathId, required_path_id: newRequiredPathId },
      ]);
      setNewRequiredPathId("");
      setNewRequiredStatus("completed");
      toast.success("Prérequis ajouté");
    } catch (e: any) {
      toast.error(e.message || "Erreur");
    } finally {
      setAdding(false);
    }
  };

  const handleStatusChange = async (prereqId: string, status: "completed" | "in_progress") => {
    setSaving(prereqId);
    try {
      const { error } = await (supabase as any)
        .from("path_prerequisite")
        .update({ required_status: status })
        .eq("id", prereqId);
      if (error) throw error;
      setPrerequisites((prev) =>
        prev.map((p) => (p.id === prereqId ? { ...p, required_status: status } : p)),
      );
    } catch (e: any) {
      toast.error(e.message || "Erreur");
    } finally {
      setSaving(null);
    }
  };

  /** Point 6 — Confirmed delete */
  const handleConfirmRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      const { error } = await (supabase as any)
        .from("path_prerequisite")
        .delete()
        .eq("id", removeTarget.id);
      if (error) throw error;
      setPrerequisites((prev) => prev.filter((p) => p.id !== removeTarget.id));
      setAllRules((prev) =>
        prev.filter(
          (r) =>
            !(r.blocked_path_id === pathId && r.required_path_id === removeTarget.required_path_id),
        ),
      );
      toast.success("Prérequis supprimé");
    } catch (e: any) {
      toast.error(e.message || "Erreur");
    } finally {
      setRemoving(false);
      setRemoveTarget(null);
    }
  };

  if (!pathId) {
    return (
      <p className="text-sm text-muted-foreground italic py-2">
        Sauvegardez le parcours avant de configurer ses prérequis.
      </p>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="animate-spin h-5 w-5 text-muted-foreground" />
      </div>
    );
  }

  const availableToAdd = publishedPaths.filter(
    (p) => p.id !== pathId && !prerequisites.some((pr) => pr.required_path_id === p.id),
  );

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-start gap-2">
          <Lock className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
          <p className="text-sm text-muted-foreground">
            Ce parcours sera verrouillé (statut{" "}
            <Badge variant="outline" className="text-xs font-mono">hold</Badge>
            ) tant que tous les prérequis ci-dessous ne sont pas remplis.
            Sans prérequis, il est accessible librement.
          </p>
        </div>

        {/* Existing prerequisites */}
        {prerequisites.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">
            Aucun prérequis — parcours accessible librement.
          </p>
        ) : (
          <div className="space-y-2">
            {prerequisites.map((prereq) => (
              <div
                key={prereq.id}
                className="flex items-center gap-2 rounded-md border px-3 py-2 bg-card"
              >
                <span
                  className="flex-1 text-sm font-medium truncate"
                  title={prereq.required_path_title}
                >
                  {prereq.required_path_title}
                </span>
                <Select
                  value={prereq.required_status}
                  disabled={disabled || saving === prereq.id}
                  onValueChange={(v) =>
                    handleStatusChange(prereq.id, v as "completed" | "in_progress")
                  }
                >
                  <SelectTrigger className="w-44 h-7 text-xs">
                    {saving === prereq.id ? (
                      <span className="flex items-center gap-1">
                        <Loader2 className="h-3 w-3 animate-spin" /> Sauvegarde…
                      </span>
                    ) : (
                      <SelectValue />
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABELS).map(([val, label]) => (
                      <SelectItem key={val} value={val} className="text-xs">
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!disabled && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:text-destructive shrink-0"
                    onClick={() => setRemoveTarget(prereq)}
                    title="Supprimer ce prérequis"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add a new prerequisite */}
        {!disabled && (
          <div className="flex items-center gap-2 pt-1">
            <Select
              value={newRequiredPathId}
              onValueChange={setNewRequiredPathId}
              disabled={adding || availableToAdd.length === 0}
            >
              <SelectTrigger className="flex-1 h-8 text-sm">
                <SelectValue
                  placeholder={
                    availableToAdd.length === 0
                      ? "Aucun parcours disponible"
                      : "Sélectionner un parcours…"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {availableToAdd.map((p) => (
                  <SelectItem key={p.id} value={p.id} className="text-sm">
                    {p.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={newRequiredStatus}
              onValueChange={(v) =>
                setNewRequiredStatus(v as "completed" | "in_progress")
              }
              disabled={adding}
            >
              <SelectTrigger className="w-44 h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_LABELS).map(([val, label]) => (
                  <SelectItem key={val} value={val} className="text-sm">
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              size="sm"
              className="h-8 gap-1.5 shrink-0"
              disabled={!newRequiredPathId || adding}
              onClick={handleAdd}
            >
              {adding ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Plus className="h-3.5 w-3.5" />
              )}
              Ajouter
            </Button>
          </div>
        )}
      </div>

      {/* Point 6 — Confirmation before delete */}
      <AlertDialog open={!!removeTarget} onOpenChange={(open) => !open && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce prérequis ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le parcours{" "}
              <span className="font-medium">«&nbsp;{removeTarget?.required_path_title}&nbsp;»</span>{" "}
              ne sera plus requis. Les utilisateurs qui avaient ce parcours verrouillé pourront y
              accéder dès la prochaine ouverture de l'app.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              disabled={removing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleConfirmRemove}
            >
              {removing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
